import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { stories } from "../data/stories";
import { useRef, useEffect, useState } from "react";

export default function StoriesSection() {
  const sliderRef = useRef(null);
  const intervalRef = useRef(null);

  const [paused, setPaused] = useState(false);

  // Duplicate stories for infinite effect
  const loopedStories = [...stories, ...stories];

  /* ================= Scroll ================= */
  const scroll = (dir = "right") => {
    const el = sliderRef.current;
    if (!el) return;

    const amount = el.offsetWidth;

    el.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  /* ================= Start from middle ================= */
  useEffect(() => {
  const el = sliderRef.current;
  if (!el) return;

  el.scrollLeft = el.offsetWidth * stories.length;
}, []);

  /* ================= Autoplay (Infinite) ================= */
  useEffect(() => {
  if (paused) return;

  const el = sliderRef.current;
  if (!el) return;

  const cardWidth = el.offsetWidth;

  intervalRef.current = setInterval(() => {
    const maxScroll = el.scrollWidth - cardWidth;

    el.scrollBy({
      left: cardWidth,
      behavior: "smooth",
    });

    // If we hit the end of duplicated slides
    setTimeout(() => {
      if (el.scrollLeft >= maxScroll) {
        // jump back to first REAL set (silent reset)
        el.scrollTo({
          left: cardWidth * stories.length,
          behavior: "auto",
        });
      }
    }, 500);
  }, 4500);

  return () => clearInterval(intervalRef.current);
}, [paused]);

  return (
    <section className="py-14 md:py-24 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="flex justify-between items-center mb-12">
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight">
            Stories of Change
          </h2>

          <Link
            to="/stories"
            className="hidden md:flex items-center gap-2 text-primary font-semibold hover:underline"
          >
            View All <ArrowRight size={16} />
          </Link>
        </div>

        {/* Slider */}
        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Left arrow */}
          <button
            onClick={() => scroll("left")}
            className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 bg-white shadow-lg p-3 rounded-full z-10 hover:scale-110 transition"
          >
            <ChevronLeft />
          </button>

          {/* Scroll container */}
          <div
            ref={sliderRef}
            className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory no-scrollbar"
          >
            {loopedStories.map((story, index) => (
              <div
                key={index}
                className="snap-start min-w-full"
              >
                <PremiumCard story={story} />
              </div>
            ))}
          </div>

          {/* Right arrow */}
          <button
            onClick={() => scroll("right")}
            className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 bg-white shadow-lg p-3 rounded-full z-10 hover:scale-110 transition"
          >
            <ChevronRight />
          </button>
        </div>

        {/* Mobile link */}
        <div className="mt-8 md:hidden text-center">
          <Link to="/stories" className="text-primary font-semibold">
            View All Stories →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ================= Premium Card ================= */

function PremiumCard({ story }) {
  return (
    <div
      className="
        flex flex-col md:flex-row
        bg-white
        rounded-2xl
        shadow-lg hover:shadow-2xl
        overflow-hidden
        transition-shadow duration-300
      "
    >
      {/* IMAGE */}
      <div className="md:w-1/2 aspect-[4/3]">
        <img
          src={story.image}
          alt={story.title}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>

      {/* DETAILS */}
      <div className="md:w-1/2 bg-primary text-white p-6 md:p-8 flex flex-col justify-center">
        <h3 className="text-lg md:text-2xl font-bold mb-3">
          {story.title}
        </h3>

        <p className="italic text-white/90 mb-5">
          “{story.description}”
        </p>

        <div className="mb-6">
          <p className="font-semibold">{story.name}</p>
          <p className="text-sm text-white/80">{story.role}</p>
        </div>

        <Link
          to={story.link}
          className="inline-flex items-center gap-2 bg-white text-primary font-semibold px-4 py-2 rounded-xl w-fit hover:bg-gray-100 transition"
        >
          Read Story <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}